import PropTypes from 'prop-types';
import { clearObject } from 'shared/utils';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { statisticsActions } from 'core/Statistics';
import { useTranslation } from 'react-i18next';
import {
    Body,
    Filter,
    FilterBox,
    Header,
    Title
} from './styled';
import {
    CompanyPicker,
    DoctorPicker,
    SpecialtyPicker
} from 'views/pickers';
import {
    Container,
    DateRangePicker,
    IconButton,
    Loader,
    SelectSearch,
    Text
} from 'mol-ui';
import {
    DownloadList,
    PatientFilters
} from 'views/components';
import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useObjectState,
    useSelector,
    useWindowSize
} from 'hooks';
const { company } = config;

const StatisticsLayout = ({
    children,
    downloadController = {},
    initialFilters = {},
    filterCompanies = true,
    filterDates = true,
    filterPatients = false,
    filterProfessionals = true,
    filterSpecialties = true,
    loading,
    onFiltersChange = () => null,
    tabController,
    title
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { xs } = useWindowSize();
    
    const { currentFilters } = useSelector('statistics');

    const downloadable = !isEmpty(downloadController);

    const DEFAULT_FILTERS = {
        company_id: null,
        doctor_id: null,
        end_date: filterDates ? moment().format('YYYY-MM-DD') : null,
        specialty_id: null,
        start_date: filterDates ? moment().set('date', 1).format('YYYY-MM-DD') : null,
        ...initialFilters
    };

    const [loaded, setLoaded] = useState(false);
    const [filters, setFilters, setFiltersValue, setFiltersValues] = useObjectState({});
    const [showFilters, setShowFilters] = useState(true);

    useEffect(() => {
        if (loaded) return;
        const { company_id, doctor_id, end_date, specialty_id, start_date } = currentFilters ?? {};
        setLoaded(true);
        setFilters({
            company_id: !filterCompanies ? undefined : company_id ?? DEFAULT_FILTERS.company_id,
            doctor_id: !filterProfessionals ? undefined : doctor_id ?? DEFAULT_FILTERS.doctor_id,
            end_date: !filterDates ? undefined : end_date ?? DEFAULT_FILTERS.end_date,
            specialty_id: !filterSpecialties ? undefined : specialty_id ?? DEFAULT_FILTERS.specialty_id,
            start_date: !filterDates ? undefined : start_date ?? DEFAULT_FILTERS.start_date
        });
    }, [currentFilters, loaded]);
    
    useEffect(() => {
        if (!loaded) return;
        dispatch(statisticsActions.setStatisticsCurrentFilters(filters));
        onFiltersChange(clearObject(filters, true));
    }, [filters]);
    
    return (
        <Container height="100%">
            <Header>
                <Title>
                    {
                        !tabController
                            ? <Text fontSize="16px" margin="0px 0px 0px 5px">
                                { title }
                            </Text>
                            : <SelectSearch
                                fontSize="13px"
                                margin="0px"
                                options={ tabController?.tabs }
                                value={ tabController?.selectedTab }
                                width="200px"
                                onChange={ tabController?.setSelectedTab }
                            />
                    }
                </Title>
                <FilterBox display={ showFilters || !xs ? undefined : 'none' }>
                    {
                        filterCompanies && company &&
                        <CompanyPicker
                            allowNone
                            height="40px"
                            margin="0px"
                            nullPlaceholder={ t('Todas las empresas') }
                            value={ filters?.company_id }
                            width={ xs ? '100%' : '200px' }
                            onChange={ value => setFiltersValue('company_id', value) }
                        />
                    }
                    {
                        filterSpecialties &&
                        <SpecialtyPicker
                            fontSize="11px"
                            height="40px"
                            margin="0px"
                            nullPlaceholder={ t('Todas las especialidades') }
                            value={ filters?.specialty_id }
                            width={ xs ? '100%' : '200px' }
                            onChange={ value => setFiltersValues({ doctor_id: null, specialty_id: value }) }
                        />
                    }
                    {
                        filterProfessionals &&
                        <DoctorPicker
                            fontSize="11px"
                            height="40px"
                            margin="0px"
                            nullPlaceholder={ t('Todos los profesionales') }
                            specialty={ filters?.specialty_id }
                            value={ filters?.doctor_id }
                            width={ xs ? '100%' : '200px' }
                            onChange={ value => setFiltersValue('doctor_id', value) }
                        />
                    }
                    {
                        filterDates &&
                        <Filter>
                            <DateRangePicker
                                allowFutureDays={ true }
                                endValue={ filters?.end_date }
                                position="right"
                                startValue={ filters?.start_date }
                                width={ xs ? '100%' : '200px' }
                                onChange={ (start_date, end_date) => setFiltersValues({ end_date, start_date }) }
                            />
                        </Filter>
                    }
                    {
                        (filterCompanies || filterDates || filterProfessionals || filterSpecialties) &&
                        <Container flexDirection="row" margin={ xs ? '0px 0px 5px 0px' : undefined }>
                            <IconButton
                                icon="clear"
                                label={ xs ? t('Limpiar') : '' }
                                margin="0px"
                                size={ xs ? 16 : 25 }
                                onClick={ () => setFilters({ ...DEFAULT_FILTERS }) }
                            />
                            <IconButton
                                icon="refresh"
                                label={ xs ? t('Actualizar') : '' }
                                margin="0px 0px 0px 5px"
                                size={ xs ? 16 : 25 }
                                onClick={ () => onFiltersChange(clearObject(filters, true)) }
                            />
                            {
                                xs &&
                                <IconButton
                                    icon="expand_less"
                                    label={ xs ? t('Ocultar') : '' }
                                    size={ xs ? 16 : 25 }
                                    onClick={ () => setShowFilters(false) }
                                />
                            }
                        </Container>
                    }
                </FilterBox>
                {
                    xs && !showFilters &&
                    <FilterBox>
                        <IconButton
                            icon="expand_more"
                            label={ t('Mostrar filtros') }
                            margin="5px 0px"
                            size={ 16 }
                            onClick={ () => setShowFilters(true) }
                        />
                    </FilterBox>
                }
            </Header>
            <Body>
                {
                    downloadable &&
                    <DownloadList
                        downloadController={ downloadController }
                        downloadFilters={ filters }
                    />
                }
                {
                    !!filterPatients &&
                    <PatientFilters
                        displayFilters={ filterPatients }
                        onFiltersChange={ setFiltersValues }
                    />
                }
                {
                    loading
                        ? <Loader active={ true } />
                        : children
                }
            </Body>
        </Container>
    );
};

StatisticsLayout.propTypes = {
    children: PropTypes.any,
    downloadController: PropTypes.object,
    filterCompanies: PropTypes.bool,
    filterDates: PropTypes.bool,
    filterPatients: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    filterProfessionals: PropTypes.bool,
    filterSpecialties: PropTypes.bool,
    initialFilters: PropTypes.object,
    loading: PropTypes.bool,
    onFiltersChange: PropTypes.func,
    tabController: PropTypes.object,
    title: PropTypes.string
};

export default StatisticsLayout;
